import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import AEOPage from './pages/AEOPage';
import Login from './pages/Login';
import Register from './pages/Register';
import PublisherDashboard from './pages/publisher/PublisherDashboard';
import PublisherProfile from './pages/publisher/PublisherProfile';
import PublisherCampaigns from './pages/publisher/PublisherCampaigns';
import PublisherEarnings from './pages/publisher/PublisherEarnings';
import PublisherMyBrands from './pages/publisher/PublisherMyBrands';
import PublisherReports from './pages/publisher/PublisherReports';
import BrandDashboard from './pages/brand/BrandDashboard';
import BrandProfile from './pages/brand/BrandProfile';
import BrandCampaigns from './pages/brand/BrandCampaigns';
import BrandReporting from './pages/brand/BrandReporting';
import BrandMyPublishers from './pages/brand/BrandMyPublishers';
import BrandPublisherContent from './pages/brand/BrandPublisherContent';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCampaigns from './pages/admin/AdminCampaigns';
import AdminBenchmarks from './pages/admin/AdminBenchmarks';
import AdminSettlements from './pages/admin/AdminSettlements';
import AdminCreateBrand from './pages/admin/AdminCreateBrand';
import AdminDirectory from './pages/admin/AdminDirectory';
import AdminReports from './pages/admin/AdminReports';
import CampaignDetail from './pages/CampaignDetail';
import { useAuth } from './hooks/useAuth';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/aeo" element={<AEOPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          
          {/* Publisher Routes */}
          <Route
            path="/publisher/dashboard"
            element={
              <ProtectedRoute allowedRoles={['publisher']}>
                <PublisherDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/publisher/profile"
            element={
              <ProtectedRoute allowedRoles={['publisher']}>
                <PublisherProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/publisher/campaigns"
            element={
              <ProtectedRoute allowedRoles={['publisher']}>
                <PublisherCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/publisher/earnings"
            element={
              <ProtectedRoute allowedRoles={['publisher']}>
                <PublisherEarnings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/publisher/my-brands"
            element={
              <ProtectedRoute allowedRoles={['publisher']}>
                <PublisherMyBrands />
              </ProtectedRoute>
            }
          />
          <Route
            path="/publisher/reports"
            element={
              <ProtectedRoute allowedRoles={['publisher']}>
                <PublisherReports />
              </ProtectedRoute>
            }
          />

          {/* Brand Routes */}
          <Route
            path="/brand/dashboard"
            element={
              <ProtectedRoute allowedRoles={['brand']}>
                <BrandDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand/profile"
            element={
              <ProtectedRoute allowedRoles={['brand']}>
                <BrandProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand/campaigns"
            element={
              <ProtectedRoute allowedRoles={['brand']}>
                <BrandCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand/reporting"
            element={
              <ProtectedRoute allowedRoles={['brand']}>
                <BrandReporting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand/my-publishers"
            element={
              <ProtectedRoute allowedRoles={['brand']}>
                <BrandMyPublishers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand/publisher-content/:publisherId"
            element={
              <ProtectedRoute allowedRoles={['brand']}>
                <BrandPublisherContent />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/campaigns"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/benchmarks"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminBenchmarks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settlements"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSettlements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/create-brand"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCreateBrand />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/directory"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDirectory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminReports />
              </ProtectedRoute>
            }
          />

          {/* Shared Routes */}
          <Route
            path="/campaigns/:campaignId"
            element={
              <ProtectedRoute>
                <CampaignDetail />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
